import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { cx } from "@/utils/cx";
import { usePrefersReducedMotion } from "./use-prefers-reduced-motion";

const DEAL_AT_MS = 620;

type DealCardState = {
  /** True once the card has dealt into place (immediately under reduced motion). */
  dealt: boolean;
  /** When this card's backdrop blur should finish revealing, in ms. */
  blurDelayMs: number;
};

type DealOutGridProps = {
  count: number;
  /** Grid column and max-width classes, e.g. "grid-cols-4 max-w-[680px]". */
  className?: string;
  children: (index: number, state: DealCardState) => ReactNode;
};

/**
 * Cards start fanned and overlapping near the centre, then deal out into their
 * grid after a beat — a slow spring on the transform, a quicker opacity fade,
 * staggered outward from the centre. Each card's backdrop blur reveals just
 * after it lands. Under reduced motion every card renders settled at once.
 */
export function DealOutGrid({ count, className, children }: DealOutGridProps) {
  const reduceMotion = usePrefersReducedMotion();
  const [dealt, setDealt] = useState(false);

  useEffect(() => {
    if (reduceMotion) return;
    const timer = setTimeout(() => setDealt(true), DEAL_AT_MS);
    return () => clearTimeout(timer);
  }, [reduceMotion]);

  const center = (count - 1) / 2;
  const settled = reduceMotion || dealt;

  return (
    <div className={cx("grid w-full gap-3", className)}>
      {Array.from({ length: count }, (_, index) => {
        const offset = index - center;
        const ring = Math.floor(Math.abs(offset));
        const staggerMs = ring * 90;
        const blurDelayMs = 780 + ring * 90;

        const wrapperStyle: CSSProperties = reduceMotion
          ? {}
          : {
              transformOrigin: "50% 85%",
              zIndex: count - index,
              opacity: settled ? 1 : 0,
              transform: settled
                ? "none"
                : `translateX(calc(${-offset * 100}% + ${-offset * 12}px)) translateY(${Math.abs(offset) * 3}px) rotate(${offset * 4}deg) scale(0.94)`,
              transition: `transform 780ms cubic-bezier(0.16, 1, 0.3, 1) ${staggerMs}ms, opacity 420ms ease-out ${staggerMs}ms`,
            };

        return (
          <div key={index} style={wrapperStyle}>
            {children(index, { dealt: settled, blurDelayMs })}
          </div>
        );
      })}
    </div>
  );
}
